/**
 * tripDayColors — one colour per driving day, shared by the itinerary (day
 * headers, stop letters) and the map (route segments, pins), so "Day 2" is
 * the same teal in both places.
 *
 * Every colour carries white text at 4.5:1 or better and sits apart from its
 * neighbours in hue and lightness, so adjacent days never read as one.
 */
import type { TripDay } from './tripPlanner';

// The hex copies of the tp.day-1 … tp.day-7 tokens (tailwind.config.js):
// the map needs literal colours, the list uses the same values.
export const DAY_COLORS = [
  '#1558b0', // tp.day-1 blue (brand)
  '#0f8a6a', // tp.day-2 green
  '#c2410c', // tp.day-3 rust
  '#7c3aed', // tp.day-4 violet
  '#a16207', // tp.day-5 ochre
  '#0e7490', // tp.day-6 cyan
  '#be185d', // tp.day-7 pink
] as const;

/** Colour of the driving day at `index` (0-based); wraps after seven days. */
export function dayColor(index: number): string {
  const n = DAY_COLORS.length;
  return DAY_COLORS[((Math.floor(index) % n) + n) % n];
}

/**
 * The driving day each stop is reached on: the first stop belongs to Day 1,
 * every other stop to the day whose drive ends at or passes through it.
 * Stops no day covers (no days yet) fall back to day 0.
 */
export function stopDayIndices(
  stopCount: number,
  days: Pick<TripDay, 'index' | 'firstStopIndex' | 'lastStopIndex'>[]
): number[] {
  const out = new Array<number>(Math.max(0, stopCount)).fill(0);
  days.forEach((d) => {
    for (let i = d.firstStopIndex + 1; i <= d.lastStopIndex && i < stopCount; i += 1) out[i] = d.index;
  });
  return out;
}
